import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { clientApi } from "../services";
import type { ClientQuickview } from "../types";

export type ClientQuickviewState = {
  clientId: number | null;
  client: ClientQuickview | null;
  loading: boolean;
  error: string | null;
};

export const initialClientQuickviewState: ClientQuickviewState = {
  clientId: null,
  client: null,
  loading: false,
  error: null,
};

export const clientQuickviewAtom = atomWithImmer<ClientQuickviewState>(
  initialClientQuickviewState,
);

const cache = new Map<number, ClientQuickview>();

export const openClientQuickviewAtom = atom(
  null,
  async (_get, set, clientId: number): Promise<void> => {
    const known = cache.get(clientId);

    set(clientQuickviewAtom, (state) => {
      const same = state.clientId === clientId;

      state.clientId = clientId;
      state.client = known ?? (same ? state.client : null);
      state.loading = undefined === known;
      state.error = null;
    });

    if (undefined !== known) {
      return;
    }

    try {
      const client = await clientApi.fetchQuickview(clientId);

      cache.set(clientId, client);

      set(clientQuickviewAtom, (state) => {
        if (state.clientId !== clientId) {
          return;
        }

        state.client = client;
        state.loading = false;
      });
    } catch (error: unknown) {
      set(clientQuickviewAtom, (state) => {
        if (state.clientId !== clientId) {
          return;
        }

        state.loading = false;
        state.error = error instanceof Error ? error.message : String(error);
      });
    }
  },
);

export const shownClientQuickviewIdAtom = atom(
  (get) => get(clientQuickviewAtom).clientId,
);

export const closeClientQuickviewAtom = atom(null, (_get, set): void => {
  set(clientQuickviewAtom, () => initialClientQuickviewState);
});

export const refreshClientQuickviewAtom = atom(
  null,
  async (get, set, clientId: number): Promise<void> => {
    cache.delete(clientId);

    if (clientId !== get(clientQuickviewAtom).clientId) {
      return;
    }

    await set(openClientQuickviewAtom, clientId);
  },
);
